// Syllable to audio lookup (Evaluar / es-mx).
//
// Mirrors phonemeToAudio.js, but for whole Spanish syllables voiced in blending
// (mmc) items. Pre-recorded mp3s are served from the CDN at
//   https://${ASSET_ROOT}/syllables/spanish/<ascii-key>.mp3
// The served prefix is locale-namespaced (syllables/spanish/) to match the existing
// phonemes/spanish/ and audio/spanish/ conventions and to keep es keys from ever
// colliding with a future locale's syllable audio on the same path.
//
// Why an allowlist instead of "build a URL and let it 404": the audio player
// (Speech.playAppSounds) treats a Howler load error the same as a normal finish,
// so a missing file would play as SILENCE, not an error we can catch. We instead
// only return a URL for syllables verified present on the CDN and return null
// otherwise, so the caller can fall back to TTS. See spanishSyllableAssets.js.
//
// Correctness note: stress accents are safe to strip (á→a), but ñ (/ɲ/) and ü (the
// /w/ glide) are DISTINCT sounds that must never collapse to their stripped forms
// (n/u) — voicing "ña" as "na" is wrong. Instead of a hand-maintained veto, we encode
// those two characters into collision-free ASCII digraphs (ñ→"nn", ü→"uu"; neither
// digraph occurs in a plain Spanish syllable) so each gets its OWN filename on
// the same /syllables/spanish/ path: ña→nna.mp3, güi→guui.mp3. The exclusion is then a
// property of the allowlist, not a special case: until such an asset is verified
// present in PRESENT_SYLLABLE_KEYS the encoded key simply isn't in the set → null →
// TTS, so an un-recorded ñ/ü syllable is still never mis-voiced. Any OTHER diacritic
// we don't explicitly encode fails the ASCII key check below → null → TTS (never a
// wrong plain-key collapse).
//
// CROSS-TEAM CONTRACT (for whoever uploads recordings and hand-edits the allowlist — there is
// no script that regenerates PRESENT_SYLLABLE_KEYS): recordings must be uploaded under this SAME
// encoding, as nna.mp3 / guui.mp3 (ASCII), never ña.mp3. Verify with
// `npm run verify:syllable-assets`, which probes the deployed origin for every allowlisted key.

import { PRESENT_SYLLABLE_KEYS } from "./spanishSyllableAssets";

const BASE_URL = process.env.ASSET_ROOT || "s3.amazonaws.com/amira-assets";

export function syllableFileToURL(relPath) {
  return "https://" + BASE_URL + "/" + relPath + ".mp3";
}

// Phonemically-distinct diacritics get collision-free ASCII digraph encodings
// (doubled letter, echoing the phoneme library's NN convention for /ɲ/). We use
// digraphs that never occur inside a plain Spanish syllable: "nn" and "uu" are
// impossible (Spanish never doubles n within a syllable, and literal "uu" never
// occurs), whereas "ue"/"ny" would collide with real keys ("bue", "gue", ...).
// "uu" is no longer injective: ü here, hiatus ú below. Unreachable — ü only ever follows g.
const PHONEMIC_DIACRITIC_ENCODINGS = { "ñ": "nn", "ü": "uu" };

// Weak+weak is a diphthong however it is stressed (huí), so i/u are not triggers.
const STRONG_VOWELS = "aeoáéó";
const isStrongVowel = (ch) => STRONG_VOWELS.includes(ch ?? " ");

// oí (o-í-do) is hiatus and needs its own file; oi (boi-na) is the diphthong.
// Must run after the ü→uu substitution, or güí stops resolving to guui.
// Loop, not a lookbehind regex: Safari <16.4 throws at parse time.
function encodeHiatusAccents(syllable) {
  let out = "";
  for (let i = 0; i < syllable.length; i++) {
    const ch = syllable[i];
    const beside = isStrongVowel(syllable[i - 1]) || isStrongVowel(syllable[i + 1]);
    if ((ch === "í" || ch === "ú") && beside) {
      out += ch === "í" ? "ii" : "uu";
    } else {
      out += ch;
    }
  }
  return out;
}

// ASCII key matching the CDN filename convention. NFC-recompose FIRST so a decomposed
// source ("n"+U+0303) is seen as "ñ" before encoding; encode ñ/ü to their digraphs and any
// hiatus-marking í/ú to theirs; then strip the remaining acute stress marks (á→a) — NOT the
// whole combining block, so any unhandled diacritic survives and is rejected by the ASCII check
// in the resolver rather than silently collapsing to a wrong plain key.
export function normalizeSyllableKey(syllable) {
  return encodeHiatusAccents(
    syllable
      .normalize("NFC")
      .toLowerCase()
      .trim()
      .replace(/[ñü]/g, (ch) => PHONEMIC_DIACRITIC_ENCODINGS[ch]),
  )
    .normalize("NFKD")
    .replace(/́/g, "") // strip acute stress accent only (á→a, í→i)
    .normalize("NFC");
}

/**
 * Resolve a single Spanish syllable to a pre-recorded mp3 URL.
 * @param {string} syllable e.g. "to", "bús", "llo", "ña"
 * @param {string} [locale] pre-recorded syllables are es-mx only — gate by locale so the
 *   resolver never fires for another locale (the syllables/spanish/ prefix is Spanish-only).
 * @returns {string|null} the mp3 URL, or null when the syllable cannot be served
 *   pre-recorded (unknown/not-yet-deployed asset, unencodable diacritic, or non-es-mx)
 *   — caller should fall back to TTS.
 */
export function getAudioForSyllable(syllable, locale = "es-mx") {
  if (!syllable) return null;
  if (locale && locale !== "es-mx") return null;

  const trimmed = syllable.trim();
  if (!trimmed) return null;

  const key = normalizeSyllableKey(trimmed);
  // Any character without an explicit ASCII encoding (unknown diacritic, non-Latin)
  // must fall to TTS — never collapse to a plausible-but-wrong filename. ñ/ü have
  // already been encoded to nn/uu above, so a surviving non-[a-z] char is unhandled.
  if (!key || !/^[a-z]+$/.test(key)) return null;

  if (PRESENT_SYLLABLE_KEYS.has(key)) {
    return syllableFileToURL("syllables/spanish/" + key);
  }
  return null;
}
